<template>
  <modal-base
    ref="modal"
    :navigate="navigate"
    :title="computedTitle"
    :width="width"
    :is-double-column="isDoubleColumn"
    :is-triple-column="isTripleColumn"
    :is-wide="isWide"
    :on-close-prompt="onClosePrompt"
    :on-close-clear-query="onCloseClearQuery"
  >
    <div class="modal-inner">
      <component ref="form" v-bind:is="formComponent" :form-data="formData"></component>
    </div>

    <template v-slot:footer>
      <template v-if="creating">
        <slot name="buttons"></slot>
        <button-outlined v-if="includeCreateAndViewRecord" @click="createThenViewRecord" :loading="saving">
          Create and View
        </button-outlined>

        <button-outlined v-if="includeCreateAndViewContact" @click="createThenViewContact" :loading="saving">
          Create and View
        </button-outlined>

        <button-default @click="save" :loading="saving">
          {{ createButtonText }}
        </button-default>
      </template>

      <template v-if="updating">
        <div class="mr-auto" v-if="! hideDeleteButton">
          <button-link
            color="is-danger"
            @click="deleteItem"
            :loading="deleting"
            :disabled="saving"
          >
            {{ deleteButtonText }}
          </button-link>
        </div>

        <slot name="buttons"></slot>

        <button-default @click="save" :loading="saving" :disabled="deleting">
          {{ saveButtonText }}
        </button-default>
      </template>
    </template>
  </modal-base>
</template>

<script>
import ModalBase from "./ModalBase";
import AppointmentForm from "../../forms/AppointmentForm";
import CallScriptStepForm from "../../forms/CallScriptStepForm";
import CampaignForm from "../../forms/CampaignForm";
import LeadInfoForm from "../../forms/LeadInfoForm";
import CampaignCostForm from "../../forms/CampaignCostForm";
import CampaignUnitCountForm from "../../forms/CampaignUnitCountForm";
import ContactForm from "../../forms/ContactForm";
import PropertyAddressForm from "../../forms/PropertyAddressForm";
import PropertyInfoForm from "../../forms/PropertyInfoForm";
import PropertyDetailsForm from "../../forms/PropertyDetailsForm";
import CustomFieldForm from "../../forms/CustomFieldForm";
import FollowUpTemplateForm from "../../forms/FollowUpTemplateForm";
import FollowUpSequenceStepTemplateForm from "../../forms/FollowUpSequenceStepTemplateForm";
import RecordForm from "../../forms/RecordForm";
import IntegrationPhoneNumberForm from "../../forms/IntegrationPhoneNumberForm";
import LeadDealForm from "../../forms/LeadDealForm";
import LeadDetailsForm from "../../forms/LeadDetailsForm";
import LeadDealProbabilityForm from "../../forms/LeadDealProbabilityForm";
import NoteForm from "../../forms/NoteForm";
import PipelineViewForm from "../../forms/PipelineViewForm";
import SnippetNoteForm from "../../forms/SnippetNoteForm";
import SnippetEmailForm from "../../forms/SnippetEmailForm";
import SnippetTextForm from "../../forms/SnippetTextForm";
import StatusForm from "../../forms/StatusForm";
import UserForm from "../../forms/UserForm";
import RecordFollowUpSequenceForm from "../../forms/RecordFollowUpSequenceForm.vue";
import ContactFollowUpSequenceForm from "../../forms/ContactFollowUpSequenceForm.vue";
import TagForm from "../../forms/TagForm";
import TagTaskForm from "../../forms/TagTaskForm";
import WebhookForm from "../../forms/WebhookForm";
import ChangeRecordDueAtForm from "../../forms/ChangeRecordDueAtForm";
import TaskForm from "../../forms/TaskForm";
import TaskListForm from "../../forms/TaskListForm.vue";
import TaskTemplateForm from "../../modules/settings/components/edit-status/TaskTemplateForm.vue";
import AddContactToRecordForm from "../../forms/AddContactToRecordForm";
import BuyingInfoForm from "../../forms/BuyingInfoForm";
import BuyingOfferForm from "../../forms/BuyingOfferForm";
import FlipAnalysisForm from "../../forms/FlipAnalysisForm";
import FlipSellingInfoForm from "../../forms/FlipSellingInfoForm";
import WholesaleAnalysisForm from "../../forms/WholesaleAnalysisForm";
import WholesaleSellingInfoForm from "../../forms/WholesaleSellingInfoForm";
import ContactTypeForm from "../../forms/ContactTypeForm";
import ContactContactTypeForm from "../../forms/ContactContactTypeForm";
import ContactCustomFieldsForm from "../../forms/ContactCustomFieldsForm";
import ContactTypeCustomFieldsForm from "../../forms/ContactTypeCustomFieldsForm";
import ShareableGroupForm from "../../forms/ShareableGroupForm";
import ShareableGroupEmailForm from "../../forms/ShareableGroupEmailForm";
import RecordingForm from "../../forms/RecordingForm";
import router from "../../router";

export default {
  name: "ModalCrudForm",
  components: {
    ModalBase,
    AppointmentForm,
    CallScriptStepForm,
    LeadInfoForm,
    CampaignForm,
    CampaignCostForm,
    CampaignUnitCountForm,
    ContactForm,
    PropertyAddressForm,
    PropertyInfoForm,
    PropertyDetailsForm,
    CustomFieldForm,
    FollowUpTemplateForm,
    FollowUpSequenceStepTemplateForm,
    RecordForm,
    IntegrationPhoneNumberForm,
    LeadDealForm,
    LeadDetailsForm,
    LeadDealProbabilityForm,
    NoteForm,
    PipelineViewForm,
    SnippetNoteForm,
    SnippetEmailForm,
    SnippetTextForm,
    StatusForm,
    UserForm,
    RecordFollowUpSequenceForm,
    ContactFollowUpSequenceForm,
    TagForm,
    TagTaskForm,
    TaskListForm,
    TaskTemplateForm,
    WebhookForm,
    ChangeRecordDueAtForm,
    TaskForm,
    BuyingInfoForm,
    BuyingOfferForm,
    FlipAnalysisForm,
    FlipSellingInfoForm,
    WholesaleAnalysisForm,
    WholesaleSellingInfoForm,
    AddContactToRecordForm,
    ContactTypeForm,
    ContactContactTypeForm,
    ContactCustomFieldsForm,
    ContactTypeCustomFieldsForm,
    ShareableGroupForm,
    ShareableGroupEmailForm,
    RecordingForm,
  },
  props: {
    title: {
      type: String,
      required: true
    },
    navigate: {
      type: String,
      required: false,
    },
    formComponent: {
      required: true
    },
    collection: {
      required: false,
      type: Array,
    },
    width: {
      type: Number,
    },
    isDoubleColumn: {
      type: Boolean,
      default: false
    },
    isTripleColumn: {
      type: Boolean,
      default: false
    },
    isWide: {
      type: Boolean,
      default: false
    },
    onClosePrompt: {
      type: Function,
      required: false,
    },
    onCloseClearQuery: {
      type: Boolean,
      default: false
    },
    hideDeleteButton: {
      type: Boolean,
      default: false
    },
    includeCreateAndViewRecord: {
      type: Boolean,
      default: false
    },
    includeCreateAndViewContact: {
      type: Boolean,
      default: false
    },
    hideTitlePrefix: {
      type: Boolean,
      default: false
    },
    createButtonText: {
      type: String,
      default: 'Create'
    },
    saveButtonText: {
      type: String,
      default: 'Save'
    },
    deleteButtonText: {
      type: String,
      default: 'Delete'
    }
  },
  data() {
    return {
      saving: false,
      deleting: false,
      formData: null,
    }
  },
  computed: {
    creating() {
      if (!this.formData) {
        return true;
      }

      return !this.formData.id;
    },
    updating() {
      return !this.creating;
    },
    computedTitle() {
      let title = "";

      if(!this.hideTitlePrefix) {
        title = (this.updating) ? 'Edit ' : 'Create ';
      }
      return title + this.title;
    }
  },
  methods: {
    createThenViewRecord() {
      return this.save().then((response) => {
        router.replace({query: {lead: response.data.id}});
      });
    },
    createThenViewContact() {
      return this.save().then((response) => {
        router.replace({query: {contact: response.data.id}});
      });
    },
    deleteItem() {
      this.deleting = true;
      return this.$refs.form.delete()
        .then(() => {
          this.$emit('deleted', this.formData)
          this.$emit('saved', this.formData)
          if (this.collection) {
            let index = this.collection.findIndex(c => c.id === this.formData.id);
            this.collection.splice(index, 1);
          }
          this.close();
        })
        .finally(() => this.deleting = false);
    },
    save() {
      this.saving = true;
      return this.$refs.form.save()
        .then((response) => {
          if (this.creating) {
            this.$emit('created', response.data)
            this.$emit('saved', response.data)
            if (this.collection) {
              this.collection.push(response.data);
            }
          } else {
            this.$emit('updated', response.data)
            this.$emit('saved', response.data)
            if (this.collection) {
              let index = this.collection.findIndex(c => c.id === response.data.id);
              this.collection.splice(index, 1, response.data);
            }
          }
          this.close();

          return response;
        })
        .finally(() => this.saving = false);
    },
    open(formData = null) {
      this.formData = formData;
      this.$refs.modal.open();
      this.$emit('open', formData);
    },
    close() {
      this.$refs.modal.close();
      this.$emit('close', this.formData);
      this.formData = null;

      if (this.navigate !== undefined) {
        this.$router.push({ name: this.navigate });
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.modal-inner {
  padding: $defaultGap;
  background-color: $white;
}
</style>
